import { ComponentProps, forwardRef, ReactNode } from "react";
import styles from "./Input.module.scss";
import { customSanitize } from "@/utils/customSanitize";
// import HCaptcha from "@hcaptcha/react-hcaptcha";
export type FormErrorState = Record<string, string[] | undefined> | null;

type InputProps = {
  errors?: FormErrorState;
  label?: string;
  children?: React.ReactNode;
  subline?: string;
  extraClass?: string;
  inputType:
    | "number"
    | "text"
    | "checkbox"
    | "tel"
    | "url"
    | "email"
    | "textarea"
    | "password";
  label_checkbox?: ReactNode;
  required_label?: string;
} & React.InputHTMLAttributes<HTMLInputElement> &
  React.TextareaHTMLAttributes<HTMLTextAreaElement>;

// TODO: extend this component to support more input types
// This component is currently used for inputs with type: text, checkbox, email, textarea
export const Input = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  InputProps
>(
  (
    {
      errors,
      label,
      subline,
      inputType,
      label_checkbox,
      required_label,
      ...props
    },
    ref,
  ) => {
    const getCheckboxLabel = () => {
      if (typeof label_checkbox === "string") {
        return (
          <span
            dangerouslySetInnerHTML={{ __html: customSanitize(label_checkbox) }}
            className="required-text pb-0"
          />
        );
      }

      return <>{label_checkbox}</>;
    };

    return (
      <FieldWrapper errors={errors} name={props.name} className={`w-full`}>
        {inputType === "checkbox" ? (
          <fieldset>
            {label && (
              <legend className="label">
                {label}{" "}
                {required_label && (
                  <span className="required-text">{required_label}</span>
                )}
              </legend>
            )}
            <label className={`${styles.checkbox}`}>
              <input
                {...props}
                ref={ref as React.Ref<HTMLInputElement>}
                type="checkbox"
                className={`${props.className ?? ""}`}
              />
              {label_checkbox && getCheckboxLabel()}
            </label>
          </fieldset>
        ) : (
          <>
            {label && (
              <label className="label" htmlFor={`input-${props.name}`}>
                {label}{" "}
                {required_label && (
                  <span className="required-text">{required_label}</span>
                )}
              </label>
            )}
            {(inputType === "text" ||
              inputType === "email" ||
              inputType === "password") && (
              <>
                <input
                  id={`input-${props.name}`}
                  {...props}
                  type={inputType}
                  ref={ref as React.Ref<HTMLInputElement>}
                  className={`input placeholder ${props.className ?? ""}`}
                />
              </>
            )}

            {inputType === "textarea" && (
              <textarea
                {...props}
                ref={ref as React.Ref<HTMLTextAreaElement>}
                className={`input placeholder ${props.className ?? ""}`}
              />
            )}
          </>
        )}

        {subline && <span className={styles.subline}>{subline}</span>}
      </FieldWrapper>
    );
  },
);

Input.displayName = "Input";

export const Checkbox = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      errors,
      children,
      extraClass,
      inputType: _ /* otherwise its passed onto the input */,
      ...props
    },
    ref,
  ) => {
    return (
      <FieldWrapper
        errors={errors}
        name={props.name}
        className={`${extraClass || ""}`}
      >
        <label className={`${styles.checkbox}`}>
          <input
            {...props}
            ref={ref}
            type="checkbox"
            className={`mr-2 ${props.className ?? ""}`}
          />
          <>{children}</>
        </label>
      </FieldWrapper>
    );
  },
);

Checkbox.displayName = "Checkbox";

interface SelectProps extends ComponentProps<"select"> {
  errors?: FormErrorState;
  children?: React.ReactNode;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ errors, children, ...props }, ref) => {
    return (
      <FieldWrapper errors={errors} name={props.name}>
        <select
          {...props}
          ref={ref}
          className={` mr-2 ${props.className ?? ""}`}
        >
          {children}
        </select>
      </FieldWrapper>
    );
  },
);

Select.displayName = "Select";

// interface HcaptchaProps {
//   errors?: FormErrorState;
//   onVerify: (token: string) => void;
// }

// export const Hcaptcha = forwardRef<HTMLDivElement, HcaptchaProps>(
//   ({ errors, onVerify }, ref) => {
//     const hasError = errors && errors["hCaptcha"];
//     const router = useRouter();
//     const language = router.locale;
//     return (
//       <div
//         className={`w-full ${styles.input} ${styles.hcaptcha} ${
//           hasError ? "has-error" : ""
//         }`}
//         ref={ref}
//       >
//         {/* <HCaptcha
//           sitekey={process.env.HCAPTCHA_SITEKEY!}
//           languageOverride={language}
//           onVerify={(token) => onVerify(token)}
//         /> */}

//         {hasError && (
//           <p className={`${styles["error-message"]}`}>{errors["hCaptcha"]}</p>
//         )}
//       </div>
//     );
//   },
// );
// Hcaptcha.displayName = "Hcaptcha";

export const Radio = forwardRef<HTMLInputElement, InputProps>(
  ({ errors, children, ...props }, ref) => {
    return (
      <FieldWrapper errors={errors} name={props.name}>
        <label className={`${styles.radio}`}>
          <input
            {...props}
            ref={ref}
            type="radio"
            className={` mr-2 ${props.className ?? ""}`}
          />
          <>{children}</>
        </label>
      </FieldWrapper>
    );
  },
);

Radio.displayName = "Radio";

interface FieldWrapperProps {
  errors: FormErrorState | undefined;
  name: string | undefined;
  children: ReactNode;
  className?: string;
}

const FieldWrapper = ({
  errors,
  name,
  children,
  className,
}: FieldWrapperProps) => {
  const hasError = errors && name && errors[name];

  return (
    <div
      className={`${styles.input} ${hasError ? "has-error" : ""} ${className}`}
    >
      {children}
      {hasError && <p className={`error`}>{hasError && errors[name!]}</p>}
    </div>
  );
};
